import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { GetAllSelfies as BLGetAllSelfies, SelectSelfie as BLSelectSelfie, DeselectSelfie as BLDeselectSelfie, DeleteSelfie as BLDeleteSelfie, ResetDinamyc as BLResetDinamyc, FormatDateToMexico, GetSelfiesPage as BLGetSelfiesPage, SendSelfiesFanIAToScreen as BLSendSelfiesFanIAToScreen, DeselectSelfieFanIA as BLDeselectSelfieFanIA } from './../../../utilities/BL/SelfieFan'
import { Loading } from '../../Commons/Loading'
import { SecondaryBase } from '../Commons/SecondaryBase'
import { ResetCurrentDynamic as BLResetCurrentDynamic } from './../../../utilities/BL/Zones'

import { useFloatMessage, MESSAGE_TYPE } from './../../Providers/FloatMessage'
import { Confirm } from './../../Commons/Modals/Confirm'
import { FormatDateTimeToDownloadImages as BLFormatDateTimeToDownloadImages } from './../../../utilities/BL/Commons/DatesAndTimes'


const PAGE_SIZE = 30;

const types = {
  SELECT: 1,
  DESELECT: 2,
  DELETE: 3,
  RESET: 4,
  IA_SHOW1: "IA_SHOW1",
  IA_SHOW_BOTH: "IA_SHOW_BOTH",
  IA_CLEAR: "IA_CLEAR",
};

const initialPagedState = {
  items: [],
  nextCursor: null,
  loading: false,
  exhausted: false,
};

const SelfieFan = ({ getOutOfTheDynamic }) => {
  const { showMessage } = useFloatMessage();

  // ----- estado general y modales -----
  const [isLoading, setIsLoading] = useState(true);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [modalType, setModalType] = useState(null);
  const [selfie, setSelfie] = useState(null); // para Fan Cam / IA (delete usa source)

  // ----- estados paginados por source -----
  const [fan, setFan] = useState({ ...initialPagedState }); // selfie_fan
  const [ia, setIA]   = useState({ ...initialPagedState }); // selfie_ia

  // refs de scroll (contenedores de lista que scrollean)
  const fanContainerRef = useRef(null);
  const iaContainerRef  = useRef(null);

  // ----- IA: pantallas y animaciones -----
  const [selectedForScreen, setSelectedForScreen] = useState({ 1: null, 2: null });
  const [animationByScreen, setAnimationByScreen] = useState({ 1: 0, 2: 0 });
  const [activeShowAction, setActiveShowAction]   = useState(null); // 'screen1'|'screen2'|null
  const [isClearingIA, setIsClearingIA]           = useState(false);

  // ----- colapsables -----
  const [isFanCollapsed, setIsFanCollapsed] = useState(true); // visible por defecto // false
  const [isIACollapsed, setIsIACollapsed]   = useState(true); // visible por defecto // false
  const fanCollapsibleRef = useRef(null); // padre con max-height
  const iaCollapsibleRef  = useRef(null); // padre con max-height

  // ========== Helpers de estado UI ==========
  const mergePage = (prev, page) => {
    const map = new Map(prev.items.map((x) => [x.id, x]));
    for (const it of page.items) map.set(it.id, { ...(map.get(it.id) || {}), ...it });
    return {
      items: Array.from(map.values()),
      nextCursor: page.nextCursor ?? null,
      loading: false,
      exhausted: !page.nextCursor,
    };
  };

  const markLoading = (prev) => ({ ...prev, loading: true });

  // Mantiene el scroll estable tras insertar/preprender
  const preserveScroll = (containerRef, updateFn) => {
    const el = containerRef.current;
    if (!el) return updateFn();
    const prevTop = el.scrollTop;
    const prevHeight = el.scrollHeight;
    updateFn();
    requestAnimationFrame(() => {
      const newHeight = el.scrollHeight;
      el.scrollTop = prevTop + (newHeight - prevHeight);
    });
  };

  // Clases para resaltar botones
  const btnClass = (base, isActive) => `${base} ${isActive ? "Btn__Adsl--Success" : ""}`;

  // Hook colapsable: ajusta max-height del padre antes de pintar y reacciona a cambios de contenido
  const useCollapsibleParent = (parentRef, contentRef, isCollapsed) => {
    useLayoutEffect(() => {
      const parent = parentRef.current;
      const content = contentRef.current;
      if (!parent || !content) return;

      parent.style.overflow = "hidden";
      parent.style.transition = "max-height .25s ease";

      const setMax = () => {
        parent.style.maxHeight = isCollapsed ? "0px" : `${content.scrollHeight}px`;
      };

      setMax(); // antes del primer paint

      const ro = new ResizeObserver(() => {
        if (!isCollapsed) {
          parent.style.maxHeight = `${content.scrollHeight}px`;
        }
      });
      ro.observe(content);
      return () => ro.disconnect();
    }, [parentRef, contentRef, isCollapsed]);
  };

  useCollapsibleParent(fanCollapsibleRef, fanContainerRef, isFanCollapsed);
  useCollapsibleParent(iaCollapsibleRef,  iaContainerRef,  isIACollapsed);

  // ======= Limpiezas cruzadas (evitan dobles resaltados) =======
  const resetIAUI = () => {
    setSelectedForScreen({ 1: null, 2: null });
    setAnimationByScreen({ 1: 0, 2: 0 });
    setActiveShowAction(null);
  };

  const resetFanUI = () => {
    setFan(prev => ({ ...prev, items: prev.items.map(x => ({ ...x, isSelected: false })) }));
  };

  const clearIA_BothSides = async () => {
    resetIAUI();
    //try { await BLDeselectSelfieFanIA(); } catch {}
  };

  const clearFan_BothSides = async () => {
    resetFanUI();
    //try { await BLDeselectSelfie(); } catch {}
  };

  // ========== Carga de páginas ==========
  const loadFirstPage = async (source) => {
    const setState = source === "selfie_fan" ? setFan : setIA;
    setState({ ...initialPagedState, loading: true });
    try {
      const resp = await BLGetSelfiesPage({ source, limit: PAGE_SIZE, cursor: null });
      if (!resp.success) throw new Error(resp.message);
      setState(mergePage(initialPagedState, { items: resp.data.items, nextCursor: resp.data.nextCursor }));
    } catch (err) {
      setState({ ...initialPagedState, loading: false });
      showMessage({ type: MESSAGE_TYPE.BAD, text: err.message || "No se pudo cargar el listado." });
    }
  };

  // Botón “Cargar más” siempre visible: con cursor → siguientes; sin cursor → recién llegadas
  const loadMore = async (source) => {
    const state     = source === "selfie_fan" ? fan : ia;
    const setState  = source === "selfie_fan" ? setFan : setIA;
    const container = source === "selfie_fan" ? fanContainerRef : iaContainerRef;

    if (state.loading) return;
    setState(markLoading);

    try {
      if (state.nextCursor) {
        const resp = await BLGetSelfiesPage({ source, limit: PAGE_SIZE, cursor: state.nextCursor });
        if (!resp.success) throw new Error(resp.message);
        const page = { items: resp.data.items, nextCursor: resp.data.nextCursor };
        preserveScroll(container, () => setState((prev) => mergePage(prev, page)));
      } else {
        const resp = await BLGetSelfiesPage({ source, limit: PAGE_SIZE, cursor: null });
        if (!resp.success) throw new Error(resp.message);

        const existingIds = new Set(state.items.map((x) => x.id));
        const latest = state.items.length
          ? new Date(Math.max(...state.items.map((x) => new Date(x.createdAtUtc).getTime())))
          : null;

        const incoming = resp.data.items;
        const onlyNew  = incoming.filter(
          (x) => !existingIds.has(x.id) && (!latest || new Date(x.createdAtUtc) > latest)
        );

        if (!onlyNew.length) {
          setState((prev) => ({ ...prev, loading: false, exhausted: true }));
          showMessage({ type: MESSAGE_TYPE.SUCCESS, text: "No hay más por ahora." });
          return;
        }

        // PREPEND nuevas manteniendo scroll
        preserveScroll(container, () =>
          //setState((prev) => ({ ...prev, items: [...onlyNew, ...prev.items], loading: false }))
          setState(prev => ({
            ...prev,
            items: [...prev.items, ...onlyNew],
            loading: false
          }))
        );
      }
    } catch (err) {
      setState((prev) => ({ ...prev, loading: false }));
      showMessage({ type: MESSAGE_TYPE.BAD, text: err.message || "No se pudo cargar más." });
    }
  };

  const reloadNow = async () => {
    await Promise.all([loadFirstPage("selfie_fan"), loadFirstPage("selfie_ia")]);
    showMessage({ type: MESSAGE_TYPE.SUCCESS, text: "Listado actualizado con éxito" });
  };

  useEffect(() => {
    (async () => {
      await Promise.all([loadFirstPage("selfie_fan"), loadFirstPage("selfie_ia")]);
      setIsLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ReturnToDashboard = async () => {
    try {
      await BLResetCurrentDynamic();
    } finally {
      getOutOfTheDynamic();
    }
  };

  // ========== Acciones Fan Cam (con modal) ==========
  const ShowConfirmModal = (selfie = null, type) => {
    setModalType(type);
    setSelfie(selfie); // en IA para delete vendrá con source: 'selfie_ia'; para IA show/clear será null
    setShowConfirmModal(true);
  };
  
  // Descarga directa (sin abrir pestaña)
  const handleDirectDownload = async (item, index) => {
    try {
      // Revisar si trae el identificador de la imagen para consultarlo más adelante
      if (!item?.contentUrl) return
      
      // Obtener la imagen de la API
      const response = await fetch(`${process.env.REACT_APP_API_URL}SelfieFan/${item.id}/content`)
      if (!response.ok) {
        showMessage({ type: MESSAGE_TYPE.BAD, text: "No fue posible descargar la imagen solicitada. Inténtalo nuevamente o inténtalo más tarde." })
      } 

      const blob = await response.blob()

      // Revisar qué extensión tiene la imagen
      const ext = item.contentType?.includes("png") ? "png" : "jpg"
      
      // Armar el nombre que tendrá la imagen
      let sourceText = ''
      if (item.source === 'selfie_ia') {
        sourceText = '_IA'
      }
      
      // Formatear la fecha y hora (hora local del usuario que descarga)
      const formattedDateTime = BLFormatDateTimeToDownloadImages(item.createdAtUtc)
      
      //const filename = `selfie_${item.id}.${ext}`
      const filename = `${index}${sourceText}_${formattedDateTime}.${ext}` // Ejemplo: 1_22102025_101532.png
      
      // Crear elemento <a> para ejecutarlo y realizar la descarga
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch {
      showMessage({ type: MESSAGE_TYPE.BAD, text: "Ha habido un error al intentar descargar la imagen solicitada. Inténtalo nuevamente o inténtalo más tarde." })
    }
  }

  // Confirmados (Fan)
  const SelectSelfie = async () => {
    if (!selfie) return { success: false };
    const s = selfie;

    // Antes de seleccionar en Fan → limpiar IA (UI + endpoint)
    await clearIA_BothSides();

    // Optimista: marcar seleccionada SOLO en fan
    preserveScroll(fanContainerRef, () =>
      setFan((prev) => ({
        ...prev,
        items: prev.items.map((x) => ({ ...x, isSelected: x.id === s.id }))
      }))
    );

    const resp = await BLSelectSelfie(s);
    if (!resp.success) {
      await loadFirstPage("selfie_fan"); // rollback
      showMessage({ type: MESSAGE_TYPE.BAD, text: resp.message || "No se pudo seleccionar la imagen." });
      return resp;
    }
    showMessage({ type: MESSAGE_TYPE.SUCCESS, text: "Mostrando exitosamente en pantalla la imagen seleccionada." });
    return resp;
  };

  const DeselectSelfie = async () => {
    if (!selfie) return { success: false };

    // Optimista: limpiar selección en fan
    preserveScroll(fanContainerRef, () =>
      setFan((prev) => ({
        ...prev,
        items: prev.items.map((x) => ({ ...x, isSelected: false }))
      }))
    );

    const resp = await BLDeselectSelfie();
    if (!resp.success) {
      await loadFirstPage("selfie_fan");
      showMessage({ type: MESSAGE_TYPE.BAD, text: resp.message || "No se pudo quitar la imagen." });
      return resp;
    }
    showMessage({ type: MESSAGE_TYPE.SUCCESS, text: "Se ha quitado la imagen seleccionada de la pantalla." });
    return resp;
  };

  const DeleteSelfie = async () => {
    if (!selfie) return { success: false };
    const s = selfie;

    // Optimista: eliminar del array correcto (fan o ia)
    if (s.source === "selfie_ia") {
      // Si la que borras estaba elegida para alguna pantalla, límpiala
      setSelectedForScreen(prev => {
        const next = { ...prev };
        if (next[1] === s.id) next[1] = null;
        if (next[2] === s.id) next[2] = null;
        return next;
      });
      setAnimationByScreen(prev => {
        const next = { ...prev };
        if (selectedForScreen[1] === s.id) next[1] = 0;
        if (selectedForScreen[2] === s.id) next[2] = 0;
        return next;
      });

      preserveScroll(iaContainerRef, () =>
        setIA((prev) => ({ ...prev, items: prev.items.filter((x) => x.id !== s.id) }))
      );
    } else {
      preserveScroll(fanContainerRef, () =>
        setFan((prev) => ({ ...prev, items: prev.items.filter((x) => x.id !== s.id) }))
      );
    }

    const resp = await BLDeleteSelfie(s);
    if (!resp.success) {
      await Promise.all([loadFirstPage("selfie_fan"), loadFirstPage("selfie_ia")]); // rollback simple
      showMessage({ type: MESSAGE_TYPE.BAD, text: resp.message || "No se pudo eliminar la selfie." });
      return resp;
    }
    showMessage({ type: MESSAGE_TYPE.SUCCESS, text: `Se ha eliminado exitosamente la selfie (${s.id}).` });
    return resp;
  };

  const ResetDinamyc = async () => {
    const resp = await BLResetDinamyc();
    if (!resp.success) {
      showMessage({ type: MESSAGE_TYPE.BAD, text: resp.message || "No se pudo reiniciar la dinámica." });
      return resp;
    }
    setFan({ ...initialPagedState });
    setIA({ ...initialPagedState });
    await Promise.all([loadFirstPage("selfie_fan"), loadFirstPage("selfie_ia")]);

    // limpiar UI IA
    resetIAUI();

    showMessage({
      type: MESSAGE_TYPE.SUCCESS,
      text: "Se ha reseteado exitosamente la dinámica. Se borraron todas las imágenes."
    });
    return resp;
  };

  // ========== Acciones IA ==========
  const toggleCardForScreen = (screenNum, cardId) => {
    setSelectedForScreen(prev => {
      const other = screenNum === 1 ? 2 : 1;
      const alreadyThere = prev[screenNum] === cardId;

      if (alreadyThere) {
        setAnimationByScreen(a => ({ ...a, [screenNum]: 0 }));
        return { ...prev, [screenNum]: null };
      }
      if (prev[other] === cardId) {
        setAnimationByScreen(a => ({ ...a, [other]: 0 }));
        return { ...prev, [other]: null, [screenNum]: cardId };
      }
      return { ...prev, [screenNum]: cardId };
    });
  };

  const setAnimationForScreen = (screenNum, animId) => {
    setAnimationByScreen(prev => ({ ...prev, [screenNum]: animId }));
  };

  // Envíos IA (con limpieza previa de Fan para evitar dobles resaltados)
  const SendSelfiesFanIAToScreen = async (screens) => {
    try {
      const dataToSend = [];
      if (screens.includes(1) && selectedForScreen[1])
        dataToSend.push({ id: selectedForScreen[1], animation: animationByScreen[1] ?? 0 });
      if (screens.includes(2) && selectedForScreen[2])
        dataToSend.push({ id: selectedForScreen[2], animation: animationByScreen[2] ?? 0 });

      if (!dataToSend.length) {
        showMessage({ type: MESSAGE_TYPE.BAD, text: "No hay imágenes para enviar a la pantalla." });
        return { success: false };
      }

      // Antes de enviar IA → limpiar Fan (UI + endpoint)
      await clearFan_BothSides();

      const response = await BLSendSelfiesFanIAToScreen(dataToSend);
      if (response?.success) {
        setActiveShowAction(screens.length === 1 ? "screen1" : "screen2");
        showMessage({
          type: MESSAGE_TYPE.SUCCESS,
          text: screens.length === 1 ? "Pantalla 1 enviada." : "Pantallas 1 y 2 enviadas.",
        });
        return response; // <— para que el modal de Confirm se cierre
      } else {
        showMessage({ type: MESSAGE_TYPE.BAD, text: response?.message || "No se pudo enviar a pantalla." });
        return response ?? { success: false };
      }
    } catch {
      showMessage({ type: MESSAGE_TYPE.BAD, text: "Error al enviar. Inténtalo nuevamente." });
      return { success: false };
    }
  };

  const ClearSelfiesFanIAFromScreen = async () => {
    if (isClearingIA) return { success: false };
    setIsClearingIA(true);
    try {
      const resp = await BLDeselectSelfieFanIA();
      if (!resp?.success) {
        showMessage({ type: MESSAGE_TYPE.BAD, text: resp?.message || "No se pudo quitar de pantalla." });
        return resp ?? { success: false };
      }
      // Reiniciar resaltados/selecciones IA
      resetIAUI();
      showMessage({ type: MESSAGE_TYPE.SUCCESS, text: "Se quitó la(s) imagen(es) de la pantalla." });
      return resp; // <— para cerrar el modal
    } catch {
      showMessage({ type: MESSAGE_TYPE.BAD, text: "Error al quitar. Intenta de nuevo." });
      return { success: false };
    } finally {
      setIsClearingIA(false);
    }
  };

  // ========== Render ==========
  if (isLoading) return <Loading />;

  return (
    <div className="SelfieFan">
      <SecondaryBase actionToExecute={ReturnToDashboard}>

        {/* Recargar ambas secciones */}
        <button
          type="button"
          className="Btn__Adsl--RedOutlineTransparent m-auto mt-4"
          onClick={reloadNow}
        >
          <i className="bi bi-arrow-counterclockwise"></i> Actualizar listado ahora
        </button>

        {/* ===================== Sección: Fan Cam ===================== */}
        <div className="SelfieFan__Admin__Section">
          {/* <img src="/img/selfiefan/Adsl-FanCam.png" className="SelfieFan__Admin__Section__Logo" alt="" /> */}
          <p className="SelfieFan__Admin__Title">Selfie Fan</p>

          {/* Total SIEMPRE visible */}
          <p className="text-center">Total de fotos: {fan.items.length}</p>

          <button
            type="button"
            className="Btn__Adsl--RedOutlineTransparent mx-auto"
            onClick={() => setIsFanCollapsed(c => !c)}
            aria-expanded={!isFanCollapsed}
          >
            {isFanCollapsed ? "Mostrar" : "Ocultar"}{" "}
            <i className={`bi ${isFanCollapsed ? "bi-caret-down-fill" : "bi-caret-up-fill"}`}></i>
          </button>

          <div ref={fanCollapsibleRef} className="Collapsible">
            <div ref={fanContainerRef}>
              {fan.items.length === 0 ? (
                <>
                  <p className="SelfieFan__Admin__Text--Center">Actualmente no hay fotos</p>
                </>
              ) : (
                <>
                  <p className="SelfieFan__Admin__Text--Center">Selecciona la foto que deseas mostrar en pantalla:</p>

                  <div className="SelfieFan__Admin__List">
                    {fan.items.map((it, index) => (
                      <div
                        key={it.id}
                        className={`SelfieFan__Admin__List__Item ${it.isSelected ? "SelfieFan__Admin__List__Item--Active" : ""}`}
                      >
                        <div className="SelfieFan__Admin__List__Item__Buttons">
                          {it.contentUrl && (
                            <button
                              type="button"
                              className="Btn__Adsl--RedOutlineTransparent SelfieFan__Admin__List__Item__Buttons__Button"
                              title="Descargar"
                              //onClick={() => handleDirectDownload(it, fan.items.length - index)}
                              onClick={() => handleDirectDownload(it, index + 1)}
                            >
                              <i className="bi bi-download"></i>
                            </button>
                          )}
                          {!it.isSelected && (
                            <button
                              type="button"
                              className="Btn__Adsl--RedOutlineTransparent SelfieFan__Admin__List__Item__Buttons__Button"
                              onClick={() => ShowConfirmModal(it, types.DELETE)}
                              title="Eliminar"
                            >
                              <i className="bi bi-trash"></i>
                            </button>
                          )}
                        </div>

                        {/* índice visual */}
                        {/* <p>#{fan.items.length - index}</p> */}
                        <p>#{index + 1}</p>

                        <img
                          src={it.thumbUrl}
                          className="SelfieFan__Admin__Image--Normal"
                          alt={it.caption || "Selfie"}
                          loading="lazy"
                        />

                        <p>Mensaje: "{it.caption}"</p>
                        <p>Fecha: {FormatDateToMexico(it.createdAtUtc)}</p>

                        {it.isSelected ? (
                          <button
                            type="button"
                            className="Btn__Adsl--Success"
                            onClick={() => ShowConfirmModal(it, types.DESELECT)}
                          >
                            <i className="bi bi-x-circle"></i> Quitar de la pantalla
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="Btn__Adsl--RedOutlineTransparent"
                            onClick={() => ShowConfirmModal(it, types.SELECT)}
                          >
                            <i className="bi bi-tv"></i> Mostrar en pantalla ahora
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}

              {/* (1) Cargar más SIEMPRE visible */}
              <div className="flex gap-2 justify-center mt-3">
                <button
                  type="button"
                  className="Btn__Adsl--RedOutlineTransparent mx-auto"
                  onClick={() => loadMore("selfie_fan")}
                  disabled={fan.loading}
                >
                  {fan.loading ? "Cargando..." : <><i className="bi bi-arrow-counterclockwise"></i> Cargar más</>}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ===================== Sección: Fan Cam IA ===================== */}
        <div className="SelfieFan__Admin__Section">
          {/* <img src="/img/selfiefan/IADSL.png" className="SelfieFan__Admin__Section__Logo" alt="" /> */}
          <p className="SelfieFan__Admin__Title">Selfie Fan con IA</p>

          {/* Total SIEMPRE visible */}
          <p className="text-center">Total de fotos: {ia.items.length}</p>

          <button
            type="button"
            className="Btn__Adsl--RedOutlineTransparent mx-auto"
            onClick={() => setIsIACollapsed(c => !c)}
            aria-expanded={!isIACollapsed}
          >
            {isIACollapsed ? "Mostrar" : "Ocultar"}{" "}
            <i className={`bi ${isIACollapsed ? "bi-caret-down-fill" : "bi-caret-up-fill"}`}></i>
          </button>

          <div ref={iaCollapsibleRef} className="Collapsible">
            <div ref={iaContainerRef}>
              {ia.items.length === 0 ? (
                <>
                  <p className="SelfieFan__Admin__Text--Center">Actualmente no hay fotos con IA</p>
                </>
              ) : (
                <>
                  {/* FakeScreen */}
                  <div className="SelfieFanIA__Admin__FakeScreen">
                    <div className="SelfieFanIA__Admin__FakeScreen__Squares">
                      {/* Pantalla 1 */}
                      <div className="SelfieFanIA__Admin__FakeScreen__Squares__Screens">
                        <p>Pantalla #1</p>

                        <img
                          src={
                            (() => {
                              const id = selectedForScreen[1];
                              const found = ia.items.find(x => x.id === id);
                              return found ? found.thumbUrl : "/img/page/Fondo-Azul-Five-Screen.png";
                              //return found ? found.thumbUrl : "/img/games/runtogoal/balon.png";
                            })()
                          }
                          className="SelfieFanIA__Admin__FakeScreen__Squares__Screens__Image"
                          alt="Pantalla 1"
                        />

                        <p>Animaciones:</p>
                        <div className="SelfieFanIA__Admin__FakeScreen__Squares__Screens__EffectButtons">
                          {[0,1,2,3].map(anim => (
                            <button
                              key={`anim1-${anim}`}
                              type="button"
                              className={btnClass(
                                "Btn__Adsl--RedOutlineTransparent SelfieFanIA__Admin__FakeScreen__Squares__Screens__EffectButtons__Buttons",
                                animationByScreen[1] === anim
                              )}
                              onClick={() => setAnimationForScreen(1, anim)}
                              disabled={!selectedForScreen[1]}
                              title={anim === 0 ? "Ninguno" : String(anim)}
                            >
                              {anim === 0 ? "Ninguno" : anim}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Pantalla 2 */}
                      <div className="SelfieFanIA__Admin__FakeScreen__Squares__Screens">
                        <p>Pantalla #2</p>

                        <img
                          src={
                            (() => {
                              const id = selectedForScreen[2];
                              const found = ia.items.find(x => x.id === id);
                              return found ? found.thumbUrl : "/img/page/Fondo-Azul-Five-Screen.png";
                              //return found ? found.thumbUrl : "/img/games/runtogoal/balon.png";
                            })()
                          }
                          className="SelfieFanIA__Admin__FakeScreen__Squares__Screens__Image"
                          alt="Pantalla 2"
                        />

                        <p>Animaciones:</p>
                        <div className="SelfieFanIA__Admin__FakeScreen__Squares__Screens__EffectButtons">
                          {[0,1,2,3].map(anim => (
                            <button
                              key={`anim2-${anim}`}
                              type="button"
                              className={btnClass(
                                "Btn__Adsl--RedOutlineTransparent SelfieFanIA__Admin__FakeScreen__Squares__Screens__EffectButtons__Buttons",
                                animationByScreen[2] === anim
                              )}
                              onClick={() => setAnimationForScreen(2, anim)}
                              disabled={!selectedForScreen[2]}
                              title={anim === 0 ? "Ninguno" : String(anim)}
                            >
                              {anim === 0 ? "Ninguno" : anim}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Acciones (con confirmaciones) */}
                    <div className="SelfieFanIA__Admin__FakeScreen__ActionButtons">
                      <button
                        type="button"
                        className={btnClass(
                          "Btn__Adsl--RedOutlineTransparent SelfieFanIA__Admin__FakeScreen__ActionButtons__Buttons",
                          activeShowAction === "screen1"
                        )}
                        disabled={!selectedForScreen[1]}
                        onClick={() => ShowConfirmModal(null, types.IA_SHOW1)}
                      >
                        Mostrar la 1 en pantalla
                      </button>

                      <button
                        type="button"
                        className={btnClass(
                          "Btn__Adsl--RedOutlineTransparent SelfieFanIA__Admin__FakeScreen__ActionButtons__Buttons",
                          activeShowAction === "screen2"
                        )}
                        disabled={!selectedForScreen[1] || !selectedForScreen[2]}
                        onClick={() => ShowConfirmModal(null, types.IA_SHOW_BOTH)}
                      >
                        Mostrar la 2 en pantalla
                      </button>

                      <button
                        type="button"
                        className="Btn__Adsl--RedOutlineTransparent SelfieFanIA__Admin__FakeScreen__ActionButtons__Buttons"
                        onClick={() => ShowConfirmModal(null, types.IA_CLEAR)}
                        disabled={isClearingIA}
                      >
                        Quitar de la pantalla
                      </button>
                    </div>
                  </div>

                  <p className="text-center mt-5">Listado de selfies:</p>
                  <div className="SelfieFanIA__Admin__List">
                    {ia.items.map((it, index) => {
                      const isFor1 = selectedForScreen[1] === it.id;
                      const isFor2 = selectedForScreen[2] === it.id;
                      const selectedForAny = isFor1 || isFor2;

                      return (
                        <div
                          key={it.id}
                          className={`SelfieFan__Admin__List__Item ${it.isSelected ? "SelfieFan__Admin__List__Item--Active" : ""}`}
                        >
                          <div className="SelfieFan__Admin__List__Item__Buttons">
                            {it.contentUrl && (
                              <button
                                type="button"
                                className="Btn__Adsl--RedOutlineTransparent SelfieFan__Admin__List__Item__Buttons__Button"
                                title="Descargar"
                                //onClick={() => handleDirectDownload(it, ia.items.length - index)}
                                onClick={() => handleDirectDownload(it, index + 1)}
                              >
                                <i className="bi bi-download"></i>
                              </button>
                            )}
                            {/* No permitir eliminar si está elegida para alguna pantalla */}
                            {(!it.isSelected && !selectedForAny) && (
                              <button
                                type="button"
                                className="Btn__Adsl--RedOutlineTransparent SelfieFan__Admin__List__Item__Buttons__Button"
                                onClick={() => ShowConfirmModal({ ...it, source: "selfie_ia" }, types.DELETE)}
                                title="Eliminar"
                              >
                                <i className="bi bi-trash"></i>
                              </button>
                            )}
                          </div>

                          {/* índice visual */}
                          {/* <p>#{ia.items.length - index}</p> */}
                          <p>#{index + 1}</p>

                          <img
                            src={it.thumbUrl}
                            className="SelfieFan__Admin__Image--Normal"
                            alt={it.caption || "Selfie"}
                            loading="lazy"
                          />

                          <p>Fecha: {FormatDateToMexico(it.createdAtUtc)}</p>

                          {/* Botones de tarjeta: Pantalla 1 / 2 */}
                          <button
                            type="button"
                            className={btnClass("Btn__Adsl--RedOutlineTransparent mx-auto", isFor1)}
                            disabled={selectedForScreen[2] === it.id}
                            onClick={() => toggleCardForScreen(1, it.id)}
                          >
                            <>
                              <i className="bi bi-plus-circle"></i> Agregar a pantalla 1
                            </>
                          </button>

                          <button
                            type="button"
                            className={btnClass("Btn__Adsl--RedOutlineTransparent mx-auto", isFor2)}
                            disabled={selectedForScreen[1] === it.id}
                            onClick={() => toggleCardForScreen(2, it.id)}
                          >
                            <>
                              <i className="bi bi-plus-circle"></i> Agregar a pantalla 2
                            </>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {/* (1) Cargar más IA SIEMPRE visible */}
              <div className="flex gap-2 justify-center mt-3">
                <button
                  type="button"
                  className="Btn__Adsl--RedOutlineTransparent mx-auto"
                  onClick={() => loadMore("selfie_ia")}
                  disabled={ia.loading}
                >
                  {ia.loading ? "Cargando..." : <><i className="bi bi-arrow-counterclockwise"></i> Cargar más</>}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Reset dinámica */}
        {/* <button
          type="button"
          className="Btn__Adsl--RedOutlineTransparent mt-4 mb-4 mx-auto"
          onClick={() => ShowConfirmModal(null, types.RESET)}
        >
          <i className="bi bi-exclamation-triangle"></i> Reiniciar la dinámica completa
        </button> */}

        {/* Modales de confirmación */}
        {showConfirmModal && (
          // Fan Cam o IA (DELETE usa s.source)
          selfie ? (
            modalType === types.SELECT ? (
              <Confirm
                message={`¿Estás segur@ de mostrar en pantalla la Selfie Fan (${selfie.id})?`}
                image={selfie.thumbUrl}
                actionToYesButton={SelectSelfie}
                textYesButton={"Sí, mostrar ahora"}
                textNoButton={"No"}
                setShowModal={setShowConfirmModal}
                setConfirmData={setSelfie}
              />
            ) : modalType === types.DESELECT ? (
              <Confirm
                message={`¿Estás segur@ de quitar de pantalla la Selfie Fan (${selfie.id})?`}
                image={selfie.thumbUrl}
                actionToYesButton={DeselectSelfie}
                textYesButton={"Sí, quitar ahora"}
                textNoButton={"No"}
                setShowModal={setShowConfirmModal}
                setConfirmData={setSelfie}
              />
            ) : modalType === types.DELETE ? (
              <Confirm
                message={`¿Estás segur@ de eliminar la Selfie (${selfie.id})?`}
                image={selfie.thumbUrl}
                actionToYesButton={DeleteSelfie}
                textYesButton={"Sí, eliminar"}
                textNoButton={"No"}
                setShowModal={setShowConfirmModal}
                setConfirmData={setSelfie}
              />
            ) : (
              modalType === types.RESET && (
                <Confirm
                  message={`¿Estás segur@ de reiniciar la dinámica completa?`}
                  image={null}
                  actionToYesButton={ResetDinamyc}
                  textYesButton={"Sí, reiniciar ahora"}
                  textNoButton={"No"}
                  setShowModal={setShowConfirmModal}
                  setConfirmData={setSelfie}
                />
              )
            )
          ) : (
            // IA (mostrar / limpiar) — devolvemos la respuesta para que el Confirm cierre
            modalType === types.IA_SHOW1 ? (
              <Confirm
                message={`¿Mostrar solo la Pantalla 1 en el estadio?`}
                image={null}
                actionToYesButton={() => SendSelfiesFanIAToScreen([1])}
                textYesButton={"Sí, mostrar 1"}
                textNoButton={"No"}
                setShowModal={setShowConfirmModal}
                setConfirmData={() => {}}
              />
            ) : modalType === types.IA_SHOW_BOTH ? (
              <Confirm
                message={`¿Mostrar la Pantalla 1 y 2 en el estadio?`}
                image={null}
                actionToYesButton={() => SendSelfiesFanIAToScreen([1,2])}
                textYesButton={"Sí, mostrar 1 y 2"}
                textNoButton={"No"}
                setShowModal={setShowConfirmModal}
                setConfirmData={() => {}}
              />
            ) : modalType === types.IA_CLEAR && (
              <Confirm
                message={`¿Quitar las imágenes de la pantalla?`}
                image={null}
                actionToYesButton={ClearSelfiesFanIAFromScreen}
                textYesButton={"Sí, quitar ahora"}
                textNoButton={"No"}
                setShowModal={setShowConfirmModal}
                setConfirmData={() => {}}
              />
            )
          )
        )}
      </SecondaryBase>
    </div>
  );
};

export { SelfieFan };























// import { useEffect, useLayoutEffect, useRef, useState } from 'react'
// import { GetAllSelfies as BLGetAllSelfies, SelectSelfie as BLSelectSelfie, DeselectSelfie as BLDeselectSelfie, DeleteSelfie as BLDeleteSelfie, ResetDinamyc as BLResetDinamyc, FormatDateToMexico, GetSelfiesPage as BLGetSelfiesPage, SendSelfiesFanIAToScreen as BLSendSelfiesFanIAToScreen, DeselectSelfieFanIA as BLDeselectSelfieFanIA } from './../../../utilities/BL/SelfieFan'
// import { Loading } from '../../Commons/Loading'
// import { SecondaryBase } from '../Commons/SecondaryBase'
// import { ResetCurrentDynamic as BLResetCurrentDynamic } from './../../../utilities/BL/Zones'

// import { useFloatMessage, MESSAGE_TYPE } from './../../Providers/FloatMessage'
// import { Confirm } from './../../Commons/Modals/Confirm'
// import { FormatDateTimeToDownloadImages as BLFormatDateTimeToDownloadImages } from './../../../utilities/BL/Commons/DatesAndTimes'


// const PAGE_SIZE = 30;

// const types = {
//   SELECT: 1,
//   DESELECT: 2,
//   DELETE: 3,
//   RESET: 4,
//   IA_SHOW1: "IA_SHOW1",
//   IA_SHOW_BOTH: "IA_SHOW_BOTH",
//   IA_CLEAR: "IA_CLEAR",
// };

// const initialPagedState = {
//   items: [],
//   nextCursor: null,
//   loading: false,
//   exhausted: false,
// };

// const SelfieFan = ({ getOutOfTheDynamic }) => {
//   const { showMessage } = useFloatMessage();

//   // ----- estado general y modales -----
//   const [isLoading, setIsLoading] = useState(true);
//   const [showConfirmModal, setShowConfirmModal] = useState(false);
//   const [modalType, setModalType] = useState(null);
//   const [selfie, setSelfie] = useState(null); // para Fan Cam / IA (delete usa source)

//   // ----- estados paginados por source -----
//   const [fan, setFan] = useState({ ...initialPagedState }); // selfie_fan
//   const [ia, setIA]   = useState({ ...initialPagedState }); // selfie_ia

//   // refs de scroll (contenedores de lista que scrollean)
//   const fanContainerRef = useRef(null);
//   const iaContainerRef  = useRef(null);

//   // ----- IA: pantallas y animaciones -----
//   const [selectedForScreen, setSelectedForScreen] = useState({ 1: null, 2: null });
//   const [animationByScreen, setAnimationByScreen] = useState({ 1: 0, 2: 0 });
//   const [activeShowAction, setActiveShowAction]   = useState(null); // 'screen1'|'screen2'|null
//   const [isClearingIA, setIsClearingIA]           = useState(false);

//   // ----- colapsables -----
//   const [isFanCollapsed, setIsFanCollapsed] = useState(false); // visible por defecto
//   const [isIACollapsed, setIsIACollapsed]   = useState(false); // visible por defecto
//   const fanCollapsibleRef = useRef(null); // padre con max-height
//   const iaCollapsibleRef  = useRef(null); // padre con max-height

//   // ========== Helpers de estado UI ==========
//   const mergePage = (prev, page) => {
//     const map = new Map(prev.items.map((x) => [x.id, x]));
//     for (const it of page.items) map.set(it.id, { ...(map.get(it.id) || {}), ...it });
//     return {
//       items: Array.from(map.values()),
//       nextCursor: page.nextCursor ?? null,
//       loading: false,
//       exhausted: !page.nextCursor,
//     };
//   };

//   const markLoading = (prev) => ({ ...prev, loading: true });

//   // Mantiene el scroll estable tras insertar/preprender
//   const preserveScroll = (containerRef, updateFn) => {
//     const el = containerRef.current;
//     if (!el) return updateFn();
//     const prevTop = el.scrollTop;
//     const prevHeight = el.scrollHeight;
//     updateFn();
//     requestAnimationFrame(() => {
//       const newHeight = el.scrollHeight;
//       el.scrollTop = prevTop + (newHeight - prevHeight);
//     });
//   };

//   // Clases para resaltar botones
//   const btnClass = (base, isActive) => `${base} ${isActive ? "Btn__Adsl--Success" : ""}`;

//   // Hook colapsable: ajusta max-height del padre antes de pintar y reacciona a cambios de contenido
//   const useCollapsibleParent = (parentRef, contentRef, isCollapsed) => {
//     useLayoutEffect(() => {
//       const parent = parentRef.current;
//       const content = contentRef.current;
//       if (!parent || !content) return;

//       parent.style.overflow = "hidden";
//       parent.style.transition = "max-height .25s ease";

//       const setMax = () => {
//         parent.style.maxHeight = isCollapsed ? "0px" : `${content.scrollHeight}px`;
//       };

//       setMax(); // antes del primer paint

//       const ro = new ResizeObserver(() => {
//         if (!isCollapsed) {
//           parent.style.maxHeight = `${content.scrollHeight}px`;
//         }
//       });
//       ro.observe(content);
//       return () => ro.disconnect();
//     }, [parentRef, contentRef, isCollapsed]);
//   };

//   useCollapsibleParent(fanCollapsibleRef, fanContainerRef, isFanCollapsed);
//   useCollapsibleParent(iaCollapsibleRef,  iaContainerRef,  isIACollapsed);

//   // ======= Limpiezas cruzadas (evitan dobles resaltados) =======
//   const resetIAUI = () => {
//     setSelectedForScreen({ 1: null, 2: null });
//     setAnimationByScreen({ 1: 0, 2: 0 });
//     setActiveShowAction(null);
//   };

//   const resetFanUI = () => {
//     setFan(prev => ({ ...prev, items: prev.items.map(x => ({ ...x, isSelected: false })) }));
//   };

//   const clearIA_BothSides = async () => {
//     resetIAUI();
//     try { await BLDeselectSelfieFanIA(); } catch {}
//   };

//   const clearFan_BothSides = async () => {
//     resetFanUI();
//     try { await BLDeselectSelfie(); } catch {}
//   };

//   // ========== Carga de páginas ==========
//   const loadFirstPage = async (source) => {
//     const setState = source === "selfie_fan" ? setFan : setIA;
//     setState({ ...initialPagedState, loading: true });
//     try {
//       const resp = await BLGetSelfiesPage({ source, limit: PAGE_SIZE, cursor: null });
//       if (!resp.success) throw new Error(resp.message);
//       setState(mergePage(initialPagedState, { items: resp.data.items, nextCursor: resp.data.nextCursor }));
//     } catch (err) {
//       setState({ ...initialPagedState, loading: false });
//       showMessage({ type: MESSAGE_TYPE.BAD, text: err.message || "No se pudo cargar el listado." });
//     }
//   };

//   // Botón “Cargar más” siempre visible: con cursor -> viejas; sin cursor -> nuevas
//   const loadMore = async (source) => {
//     const state     = source === "selfie_fan" ? fan : ia;
//     const setState  = source === "selfie_fan" ? setFan : setIA;
//     const container = source === "selfie_fan" ? fanContainerRef : iaContainerRef;

//     if (state.loading) return;
//     setState(markLoading);

//     try {
//       if (state.nextCursor) {
//         const resp = await BLGetSelfiesPage({ source, limit: PAGE_SIZE, cursor: state.nextCursor });
//         if (!resp.success) throw new Error(resp.message);
//         const page = { items: resp.data.items, nextCursor: resp.data.nextCursor };
//         preserveScroll(container, () => setState((prev) => mergePage(prev, page)));
//       } else {
//         const resp = await BLGetSelfiesPage({ source, limit: PAGE_SIZE, cursor: null });
//         if (!resp.success) throw new Error(resp.message);

//         const existingIds = new Set(state.items.map((x) => x.id));
//         const latest = state.items.length
//           ? new Date(Math.max(...state.items.map((x) => new Date(x.createdAtUtc).getTime())))
//           : null;

//         const incoming = resp.data.items;
//         const onlyNew  = incoming.filter(
//           (x) => !existingIds.has(x.id) && (!latest || new Date(x.createdAtUtc) > latest)
//         );

//         if (!onlyNew.length) {
//           setState((prev) => ({ ...prev, loading: false, exhausted: true }));
//           showMessage({ type: MESSAGE_TYPE.SUCCESS, text: "No hay más por ahora." });
//           return;
//         }

//         // PREPEND nuevas manteniendo scroll
//         preserveScroll(container, () =>
//           setState((prev) => ({ ...prev, items: [...onlyNew, ...prev.items], loading: false }))
//         );
//       }
//     } catch (err) {
//       setState((prev) => ({ ...prev, loading: false }));
//       showMessage({ type: MESSAGE_TYPE.BAD, text: err.message || "No se pudo cargar más." });
//     }
//   };

//   const reloadNow = async () => {
//     await Promise.all([loadFirstPage("selfie_fan"), loadFirstPage("selfie_ia")]);
//     showMessage({ type: MESSAGE_TYPE.SUCCESS, text: "Listado actualizado con éxito" });
//   };

//   useEffect(() => {
//     (async () => {
//       await Promise.all([loadFirstPage("selfie_fan"), loadFirstPage("selfie_ia")]);
//       setIsLoading(false);
//     })();
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, []);

//   const ReturnToDashboard = async () => {
//     try {
//       await BLResetCurrentDynamic();
//     } finally {
//       getOutOfTheDynamic();
//     }
//   };

//   // ========== Acciones Fan Cam (con modal) ==========
//   const ShowConfirmModal = (selfie = null, type) => {
//     setModalType(type);
//     setSelfie(selfie); // en IA para delete vendrá con source: 'selfie_ia'; para IA show/clear será null
//     setShowConfirmModal(true);
//   };
  
//   // Descarga directa (sin abrir pestaña)
//   const handleDirectDownload = async (item, index) => {
//     try {
//       debugger
//       // Revisar si trae el identificador de la imagen para consultarlo más adelante
//       if (!item?.contentUrl) return
      
//       // Obtener la imagen de la API
//       const response = await fetch(`${process.env.REACT_APP_API_URL}SelfieFan/${item.id}/content`)
//       if (!response.ok) {
//         showMessage({ type: MESSAGE_TYPE.BAD, text: "No fue posible descargar la imagen solicitada. Inténtalo nuevamente o inténtalo más tarde." })
//       } 

//       const blob = await response.blob()

//       // Revisar qué extensión tiene la imagen
//       const ext = item.contentType?.includes("png") ? "png" : "jpg"
      
//       // Armar el nombre que tendrá la imagen
//       let sourceText = ''
//       if (item.source === 'selfie_ia') {
//         sourceText = '_IA'
//       }
      
//       // Formatear la fecha y hora (hora local del usuario que descarga)
//       const formattedDateTime = BLFormatDateTimeToDownloadImages(item.createdAtUtc)
      
//       //const filename = `selfie_${item.id}.${ext}`
//       const filename = `${index}${sourceText}_${formattedDateTime}.${ext}` // Ejemplo: 1_22102025_101532.png
      
//       // Crear elemento <a> para ejecutarlo y realizar la descarga
//       const url = URL.createObjectURL(blob)
//       const a = document.createElement("a")
//       a.href = url
//       a.download = filename
//       document.body.appendChild(a)
//       a.click()
//       a.remove()
//       URL.revokeObjectURL(url)
//     } catch {
//       showMessage({ type: MESSAGE_TYPE.BAD, text: "Ha habido un error al intentar descargar la imagen solicitada. Inténtalo nuevamente o inténtalo más tarde." })
//     }
//   }

//   // Confirmados (Fan)
//   const SelectSelfie = async () => {
//     if (!selfie) return { success: false };
//     const s = selfie;

//     // Antes de seleccionar en Fan → limpiar IA (UI + endpoint)
//     await clearIA_BothSides();

//     // Optimista: marcar seleccionada SOLO en fan
//     preserveScroll(fanContainerRef, () =>
//       setFan((prev) => ({
//         ...prev,
//         items: prev.items.map((x) => ({ ...x, isSelected: x.id === s.id }))
//       }))
//     );

//     const resp = await BLSelectSelfie(s);
//     if (!resp.success) {
//       await loadFirstPage("selfie_fan"); // rollback
//       showMessage({ type: MESSAGE_TYPE.BAD, text: resp.message || "No se pudo seleccionar la imagen." });
//       return resp;
//     }
//     showMessage({ type: MESSAGE_TYPE.SUCCESS, text: "Mostrando exitosamente en pantalla la imagen seleccionada." });
//     return resp;
//   };

//   const DeselectSelfie = async () => {
//     if (!selfie) return { success: false };

//     // Optimista: limpiar selección en fan
//     preserveScroll(fanContainerRef, () =>
//       setFan((prev) => ({
//         ...prev,
//         items: prev.items.map((x) => ({ ...x, isSelected: false }))
//       }))
//     );

//     const resp = await BLDeselectSelfie();
//     if (!resp.success) {
//       await loadFirstPage("selfie_fan");
//       showMessage({ type: MESSAGE_TYPE.BAD, text: resp.message || "No se pudo quitar la imagen." });
//       return resp;
//     }
//     showMessage({ type: MESSAGE_TYPE.SUCCESS, text: "Se ha quitado la imagen seleccionada de la pantalla." });
//     return resp;
//   };

//   const DeleteSelfie = async () => {
//     if (!selfie) return { success: false };
//     const s = selfie;

//     // Optimista: eliminar del array correcto (fan o ia)
//     if (s.source === "selfie_ia") {
//       // Si la que borras estaba elegida para alguna pantalla, límpiala
//       setSelectedForScreen(prev => {
//         const next = { ...prev };
//         if (next[1] === s.id) next[1] = null;
//         if (next[2] === s.id) next[2] = null;
//         return next;
//       });
//       setAnimationByScreen(prev => {
//         const next = { ...prev };
//         if (selectedForScreen[1] === s.id) next[1] = 0;
//         if (selectedForScreen[2] === s.id) next[2] = 0;
//         return next;
//       });

//       preserveScroll(iaContainerRef, () =>
//         setIA((prev) => ({ ...prev, items: prev.items.filter((x) => x.id !== s.id) }))
//       );
//     } else {
//       preserveScroll(fanContainerRef, () =>
//         setFan((prev) => ({ ...prev, items: prev.items.filter((x) => x.id !== s.id) }))
//       );
//     }

//     const resp = await BLDeleteSelfie(s);
//     if (!resp.success) {
//       await Promise.all([loadFirstPage("selfie_fan"), loadFirstPage("selfie_ia")]); // rollback simple
//       showMessage({ type: MESSAGE_TYPE.BAD, text: resp.message || "No se pudo eliminar la selfie." });
//       return resp;
//     }
//     showMessage({ type: MESSAGE_TYPE.SUCCESS, text: `Se ha eliminado exitosamente la selfie (${s.id}).` });
//     return resp;
//   };

//   const ResetDinamyc = async () => {
//     const resp = await BLResetDinamyc();
//     if (!resp.success) {
//       showMessage({ type: MESSAGE_TYPE.BAD, text: resp.message || "No se pudo reiniciar la dinámica." });
//       return resp;
//     }
//     setFan({ ...initialPagedState });
//     setIA({ ...initialPagedState });
//     await Promise.all([loadFirstPage("selfie_fan"), loadFirstPage("selfie_ia")]);

//     // limpiar UI IA
//     resetIAUI();

//     showMessage({
//       type: MESSAGE_TYPE.SUCCESS,
//       text: "Se ha reseteado exitosamente la dinámica. Se borraron todas las imágenes."
//     });
//     return resp;
//   };

//   // ========== Acciones IA ==========
//   const toggleCardForScreen = (screenNum, cardId) => {
//     setSelectedForScreen(prev => {
//       const other = screenNum === 1 ? 2 : 1;
//       const alreadyThere = prev[screenNum] === cardId;

//       if (alreadyThere) {
//         setAnimationByScreen(a => ({ ...a, [screenNum]: 0 }));
//         return { ...prev, [screenNum]: null };
//       }
//       if (prev[other] === cardId) {
//         setAnimationByScreen(a => ({ ...a, [other]: 0 }));
//         return { ...prev, [other]: null, [screenNum]: cardId };
//       }
//       return { ...prev, [screenNum]: cardId };
//     });
//   };

//   const setAnimationForScreen = (screenNum, animId) => {
//     setAnimationByScreen(prev => ({ ...prev, [screenNum]: animId }));
//   };

//   // Envíos IA (con limpieza previa de Fan para evitar dobles resaltados)
//   const SendSelfiesFanIAToScreen = async (screens) => {
//     try {
//       const dataToSend = [];
//       if (screens.includes(1) && selectedForScreen[1])
//         dataToSend.push({ id: selectedForScreen[1], animation: animationByScreen[1] ?? 0 });
//       if (screens.includes(2) && selectedForScreen[2])
//         dataToSend.push({ id: selectedForScreen[2], animation: animationByScreen[2] ?? 0 });

//       if (!dataToSend.length) {
//         showMessage({ type: MESSAGE_TYPE.BAD, text: "No hay imágenes para enviar a la pantalla." });
//         return { success: false };
//       }

//       // Antes de enviar IA → limpiar Fan (UI + endpoint)
//       await clearFan_BothSides();

//       const response = await BLSendSelfiesFanIAToScreen(dataToSend);
//       if (response?.success) {
//         setActiveShowAction(screens.length === 1 ? "screen1" : "screen2");
//         showMessage({
//           type: MESSAGE_TYPE.SUCCESS,
//           text: screens.length === 1 ? "Pantalla 1 enviada." : "Pantallas 1 y 2 enviadas.",
//         });
//         return response; // <— para que el modal de Confirm se cierre
//       } else {
//         showMessage({ type: MESSAGE_TYPE.BAD, text: response?.message || "No se pudo enviar a pantalla." });
//         return response ?? { success: false };
//       }
//     } catch {
//       showMessage({ type: MESSAGE_TYPE.BAD, text: "Error al enviar. Inténtalo nuevamente." });
//       return { success: false };
//     }
//   };

//   const ClearSelfiesFanIAFromScreen = async () => {
//     if (isClearingIA) return { success: false };
//     setIsClearingIA(true);
//     try {
//       const resp = await BLDeselectSelfieFanIA();
//       if (!resp?.success) {
//         showMessage({ type: MESSAGE_TYPE.BAD, text: resp?.message || "No se pudo quitar de pantalla." });
//         return resp ?? { success: false };
//       }
//       // Reiniciar resaltados/selecciones IA
//       resetIAUI();
//       showMessage({ type: MESSAGE_TYPE.SUCCESS, text: "Se quitó la(s) imagen(es) de la pantalla." });
//       return resp; // <— para cerrar el modal
//     } catch {
//       showMessage({ type: MESSAGE_TYPE.BAD, text: "Error al quitar. Intenta de nuevo." });
//       return { success: false };
//     } finally {
//       setIsClearingIA(false);
//     }
//   };

//   // ========== Render ==========
//   if (isLoading) return <Loading />;

//   return (
//     <div className="SelfieFan">
//       <SecondaryBase actionToExecute={ReturnToDashboard}>

//         {/* Recargar ambas secciones */}
//         <button
//           type="button"
//           className="Btn__Adsl--RedOutlineTransparent m-auto mt-4"
//           onClick={reloadNow}
//         >
//           <i className="bi bi-arrow-counterclockwise"></i> Actualizar listado ahora
//         </button>

//         {/* ===================== Sección: Fan Cam ===================== */}
//         <div className="SelfieFan__Admin__Section">
//           <img src="/img/selfiefan/Adsl-FanCam.png" className="SelfieFan__Admin__Section__Logo" alt="" />
//           <p className="SelfieFan__Admin__Title">Selfie Fan</p>

//           {/* Total SIEMPRE visible */}
//           <p className="text-center">Total de fotos: {fan.items.length}</p>

//           <button
//             type="button"
//             className="Btn__Adsl--RedOutlineTransparent mx-auto"
//             onClick={() => setIsFanCollapsed(c => !c)}
//             aria-expanded={!isFanCollapsed}
//           >
//             {isFanCollapsed ? "Mostrar" : "Ocultar"}{" "}
//             <i className={`bi ${isFanCollapsed ? "bi-caret-down-fill" : "bi-caret-up-fill"}`}></i>
//           </button>

//           <div ref={fanCollapsibleRef} className="Collapsible">
//             <div ref={fanContainerRef}>
//               {fan.items.length === 0 ? (
//                 <>
//                   <p className="SelfieFan__Admin__Text--Center">Actualmente no hay fotos</p>
//                 </>
//               ) : (
//                 <>
//                   <p className="SelfieFan__Admin__Text--Center">Selecciona la foto que deseas mostrar en pantalla:</p>

//                   <div className="SelfieFan__Admin__List">
//                     {fan.items.map((it, index) => (
//                       <div
//                         key={it.id}
//                         className={`SelfieFan__Admin__List__Item ${it.isSelected ? "SelfieFan__Admin__List__Item--Active" : ""}`}
//                       >
//                         <div className="SelfieFan__Admin__List__Item__Buttons">
//                           {it.contentUrl && (
//                             <button
//                               type="button"
//                               className="Btn__Adsl--RedOutlineTransparent SelfieFan__Admin__List__Item__Buttons__Button"
//                               title="Descargar"
//                               onClick={() => handleDirectDownload(it, fan.items.length - index)}
//                             >
//                               <i className="bi bi-download"></i>
//                             </button>
//                           )}
//                           {!it.isSelected && (
//                             <button
//                               type="button"
//                               className="Btn__Adsl--RedOutlineTransparent SelfieFan__Admin__List__Item__Buttons__Button"
//                               onClick={() => ShowConfirmModal(it, types.DELETE)}
//                               title="Eliminar"
//                             >
//                               <i className="bi bi-trash"></i>
//                             </button>
//                           )}
//                         </div>

//                         {/* índice visual */}
//                         <p>#{fan.items.length - index}</p>

//                         <img
//                           src={it.thumbUrl}
//                           className="SelfieFan__Admin__Image--Normal"
//                           alt={it.caption || "Selfie"}
//                           loading="lazy"
//                         />

//                         <p>Mensaje: "{it.caption}"</p>
//                         <p>Fecha: {FormatDateToMexico(it.createdAtUtc)}</p>

//                         {it.isSelected ? (
//                           <button
//                             type="button"
//                             className="Btn__Adsl--Success"
//                             onClick={() => ShowConfirmModal(it, types.DESELECT)}
//                           >
//                             Quitar de la pantalla
//                           </button>
//                         ) : (
//                           <button
//                             type="button"
//                             className="Btn__Adsl--RedOutlineTransparent"
//                             onClick={() => ShowConfirmModal(it, types.SELECT)}
//                           >
//                             Mostrar en pantalla
//                           </button>
//                         )}
//                       </div>
//                     ))}
//                   </div>
//                 </>
//               )}

//               {/* (1) Cargar más SIEMPRE visible */}
//               <div className="flex gap-2 justify-center mt-3">
//                 <button
//                   type="button"
//                   className="Btn__Adsl--RedOutlineTransparent mx-auto"
//                   onClick={() => loadMore("selfie_fan")}
//                   disabled={fan.loading}
//                 >
//                   {fan.loading ? "Cargando..." : "Cargar más"}
//                 </button>
//               </div>
//             </div>
//           </div>
//         </div>

//         {/* ===================== Sección: Fan Cam IA ===================== */}
//         <div className="SelfieFan__Admin__Section">
//           <img src="/img/selfiefan/IADSL.png" className="SelfieFan__Admin__Section__Logo" alt="" />
//           <p className="SelfieFan__Admin__Title">Selfie Fan con IA</p>

//           {/* Total SIEMPRE visible */}
//           <p className="text-center">Total de fotos: {ia.items.length}</p>

//           <button
//             type="button"
//             className="Btn__Adsl--RedOutlineTransparent mx-auto"
//             onClick={() => setIsIACollapsed(c => !c)}
//             aria-expanded={!isIACollapsed}
//           >
//             {isIACollapsed ? "Mostrar" : "Ocultar"}{" "}
//             <i className={`bi ${isIACollapsed ? "bi-caret-down-fill" : "bi-caret-up-fill"}`}></i>
//           </button>

//           <div ref={iaCollapsibleRef} className="Collapsible">
//             <div ref={iaContainerRef}>
//               {ia.items.length === 0 ? (
//                 <>
//                   <p className="SelfieFan__Admin__Text--Center">Actualmente no hay fotos con IA</p>
//                 </>
//               ) : (
//                 <>
//                   {/* FakeScreen */}
//                   <div className="SelfieFanIA__Admin__FakeScreen">
//                     <div className="SelfieFanIA__Admin__FakeScreen__Squares">
//                       {/* Pantalla 1 */}
//                       <div className="SelfieFanIA__Admin__FakeScreen__Squares__Screens">
//                         <p>Pantalla #1</p>

//                         <img
//                           src={
//                             (() => {
//                               const id = selectedForScreen[1];
//                               const found = ia.items.find(x => x.id === id);
//                               return found ? found.thumbUrl : "/img/games/runtogoal/balon.png";
//                             })()
//                           }
//                           className="SelfieFanIA__Admin__FakeScreen__Squares__Screens__Image"
//                           alt="Pantalla 1"
//                         />

//                         <p>Animaciones:</p>
//                         <div className="SelfieFanIA__Admin__FakeScreen__Squares__Screens__EffectButtons">
//                           {[0,1,2,3].map(anim => (
//                             <button
//                               key={`anim1-${anim}`}
//                               type="button"
//                               className={btnClass(
//                                 "Btn__Adsl--RedOutlineTransparent SelfieFanIA__Admin__FakeScreen__Squares__Screens__EffectButtons__Buttons",
//                                 animationByScreen[1] === anim
//                               )}
//                               onClick={() => setAnimationForScreen(1, anim)}
//                               disabled={!selectedForScreen[1]}
//                               title={anim === 0 ? "Ninguno" : String(anim)}
//                             >
//                               {anim === 0 ? "Ninguno" : anim}
//                             </button>
//                           ))}
//                         </div>
//                       </div>

//                       {/* Pantalla 2 */}
//                       <div className="SelfieFanIA__Admin__FakeScreen__Squares__Screens">
//                         <p>Pantalla #2</p>

//                         <img
//                           src={
//                             (() => {
//                               const id = selectedForScreen[2];
//                               const found = ia.items.find(x => x.id === id);
//                               return found ? found.thumbUrl : "/img/games/runtogoal/balon.png";
//                             })()
//                           }
//                           className="SelfieFanIA__Admin__FakeScreen__Squares__Screens__Image"
//                           alt="Pantalla 2"
//                         />

//                         <p>Animaciones:</p>
//                         <div className="SelfieFanIA__Admin__FakeScreen__Squares__Screens__EffectButtons">
//                           {[0,1,2,3].map(anim => (
//                             <button
//                               key={`anim2-${anim}`}
//                               type="button"
//                               className={btnClass(
//                                 "Btn__Adsl--RedOutlineTransparent SelfieFanIA__Admin__FakeScreen__Squares__Screens__EffectButtons__Buttons",
//                                 animationByScreen[2] === anim
//                               )}
//                               onClick={() => setAnimationForScreen(2, anim)}
//                               disabled={!selectedForScreen[2]}
//                               title={anim === 0 ? "Ninguno" : String(anim)}
//                             >
//                               {anim === 0 ? "Ninguno" : anim}
//                             </button>
//                           ))}
//                         </div>
//                       </div>
//                     </div>

//                     {/* Acciones (con confirmaciones) */}
//                     <div className="SelfieFanIA__Admin__FakeScreen__ActionButtons">
//                       <button
//                         type="button"
//                         className={btnClass(
//                           "Btn__Adsl--RedOutlineTransparent SelfieFanIA__Admin__FakeScreen__ActionButtons__Buttons",
//                           activeShowAction === "screen1"
//                         )}
//                         disabled={!selectedForScreen[1]}
//                         onClick={() => ShowConfirmModal(null, types.IA_SHOW1)}
//                       >
//                         Mostrar la 1 en pantalla
//                       </button>

//                       <button
//                         type="button"
//                         className={btnClass(
//                           "Btn__Adsl--RedOutlineTransparent SelfieFanIA__Admin__FakeScreen__ActionButtons__Buttons",
//                           activeShowAction === "screen2"
//                         )}
//                         disabled={!selectedForScreen[1] || !selectedForScreen[2]}
//                         onClick={() => ShowConfirmModal(null, types.IA_SHOW_BOTH)}
//                       >
//                         Mostrar la 2 en pantalla
//                       </button>

//                       <button
//                         type="button"
//                         className="Btn__Adsl--RedOutlineTransparent SelfieFanIA__Admin__FakeScreen__ActionButtons__Buttons"
//                         onClick={() => ShowConfirmModal(null, types.IA_CLEAR)}
//                         disabled={isClearingIA}
//                       >
//                         Quitar de la pantalla
//                       </button>
//                     </div>
//                   </div>

//                   <p className="text-center mt-5">Listado de selfies:</p>
//                   <div className="SelfieFanIA__Admin__List">
//                     {ia.items.map((it, index) => {
//                       const isFor1 = selectedForScreen[1] === it.id;
//                       const isFor2 = selectedForScreen[2] === it.id;
//                       const selectedForAny = isFor1 || isFor2;

//                       return (
//                         <div
//                           key={it.id}
//                           className={`SelfieFan__Admin__List__Item ${it.isSelected ? "SelfieFan__Admin__List__Item--Active" : ""}`}
//                         >
//                           <div className="SelfieFan__Admin__List__Item__Buttons">
//                             {it.contentUrl && (
//                               <button
//                                 type="button"
//                                 className="Btn__Adsl--RedOutlineTransparent SelfieFan__Admin__List__Item__Buttons__Button"
//                                 title="Descargar"
//                                 onClick={() => handleDirectDownload(it, ia.items.length - index)}
//                               >
//                                 <i className="bi bi-download"></i>
//                               </button>
//                             )}
//                             {/* No permitir eliminar si está elegida para alguna pantalla */}
//                             {(!it.isSelected && !selectedForAny) && (
//                               <button
//                                 type="button"
//                                 className="Btn__Adsl--RedOutlineTransparent SelfieFan__Admin__List__Item__Buttons__Button"
//                                 onClick={() => ShowConfirmModal({ ...it, source: "selfie_ia" }, types.DELETE)}
//                                 title="Eliminar"
//                               >
//                                 <i className="bi bi-trash"></i>
//                               </button>
//                             )}
//                           </div>

//                           {/* índice visual */}
//                           <p>#{ia.items.length - index}</p>

//                           <img
//                             src={it.thumbUrl}
//                             className="SelfieFan__Admin__Image--Normal"
//                             alt={it.caption || "Selfie"}
//                             loading="lazy"
//                           />

//                           <p>Fecha: {FormatDateToMexico(it.createdAtUtc)}</p>

//                           {/* Botones de tarjeta: Pantalla 1 / 2 */}
//                           <button
//                             type="button"
//                             className={btnClass("Btn__Adsl--RedOutlineTransparent mx-auto", isFor1)}
//                             disabled={selectedForScreen[2] === it.id}
//                             onClick={() => toggleCardForScreen(1, it.id)}
//                           >
//                             Pantalla 1
//                           </button>

//                           <button
//                             type="button"
//                             className={btnClass("Btn__Adsl--RedOutlineTransparent mx-auto", isFor2)}
//                             disabled={selectedForScreen[1] === it.id}
//                             onClick={() => toggleCardForScreen(2, it.id)}
//                           >
//                             Pantalla 2
//                           </button>
//                         </div>
//                       );
//                     })}
//                   </div>
//                 </>
//               )}

//               {/* (1) Cargar más IA SIEMPRE visible */}
//               <div className="flex gap-2 justify-center mt-3">
//                 <button
//                   type="button"
//                   className="Btn__Adsl--RedOutlineTransparent mx-auto"
//                   onClick={() => loadMore("selfie_ia")}
//                   disabled={ia.loading}
//                 >
//                   {ia.loading ? "Cargando..." : "Cargar más"}
//                 </button>
//               </div>
//             </div>
//           </div>
//         </div>

//         {/* Reset dinámica */}
//         <button
//           type="button"
//           className="Btn__Adsl--RedOutlineTransparent mt-4 mb-4 mx-auto"
//           onClick={() => ShowConfirmModal(null, types.RESET)}
//         >
//           <i className="bi bi-exclamation-triangle"></i> Reiniciar la dinámica completa
//         </button>

//         {/* Modales de confirmación */}
//         {showConfirmModal && (
//           // Fan Cam o IA (DELETE usa s.source)
//           selfie ? (
//             modalType === types.SELECT ? (
//               <Confirm
//                 message={`¿Estás segur@ de mostrar en pantalla la Selfie Fan (${selfie.id})?`}
//                 image={selfie.thumbUrl}
//                 actionToYesButton={SelectSelfie}
//                 textYesButton={"Sí, mostrar ahora"}
//                 textNoButton={"No"}
//                 setShowModal={setShowConfirmModal}
//                 setConfirmData={setSelfie}
//               />
//             ) : modalType === types.DESELECT ? (
//               <Confirm
//                 message={`¿Estás segur@ de quitar de pantalla la Selfie Fan (${selfie.id})?`}
//                 image={selfie.thumbUrl}
//                 actionToYesButton={DeselectSelfie}
//                 textYesButton={"Sí, quitar ahora"}
//                 textNoButton={"No"}
//                 setShowModal={setShowConfirmModal}
//                 setConfirmData={setSelfie}
//               />
//             ) : modalType === types.DELETE ? (
//               <Confirm
//                 message={`¿Estás segur@ de eliminar la Selfie (${selfie.id})?`}
//                 image={selfie.thumbUrl}
//                 actionToYesButton={DeleteSelfie}
//                 textYesButton={"Sí, eliminar"}
//                 textNoButton={"No"}
//                 setShowModal={setShowConfirmModal}
//                 setConfirmData={setSelfie}
//               />
//             ) : (
//               modalType === types.RESET && (
//                 <Confirm
//                   message={`¿Estás segur@ de reiniciar la dinámica completa?`}
//                   image={null}
//                   actionToYesButton={ResetDinamyc}
//                   textYesButton={"Sí, reiniciar ahora"}
//                   textNoButton={"No"}
//                   setShowModal={setShowConfirmModal}
//                   setConfirmData={setSelfie}
//                 />
//               )
//             )
//           ) : (
//             // IA (mostrar / limpiar) — devolvemos la respuesta para que el Confirm cierre
//             modalType === types.IA_SHOW1 ? (
//               <Confirm
//                 message={`¿Mostrar solo la Pantalla 1 en el estadio?`}
//                 image={null}
//                 actionToYesButton={() => SendSelfiesFanIAToScreen([1])}
//                 textYesButton={"Sí, mostrar 1"}
//                 textNoButton={"No"}
//                 setShowModal={setShowConfirmModal}
//                 setConfirmData={() => {}}
//               />
//             ) : modalType === types.IA_SHOW_BOTH ? (
//               <Confirm
//                 message={`¿Mostrar la Pantalla 1 y 2 en el estadio?`}
//                 image={null}
//                 actionToYesButton={() => SendSelfiesFanIAToScreen([1,2])}
//                 textYesButton={"Sí, mostrar 1 y 2"}
//                 textNoButton={"No"}
//                 setShowModal={setShowConfirmModal}
//                 setConfirmData={() => {}}
//               />
//             ) : modalType === types.IA_CLEAR && (
//               <Confirm
//                 message={`¿Quitar las imágenes de la pantalla?`}
//                 image={null}
//                 actionToYesButton={ClearSelfiesFanIAFromScreen}
//                 textYesButton={"Sí, quitar ahora"}
//                 textNoButton={"No"}
//                 setShowModal={setShowConfirmModal}
//                 setConfirmData={() => {}}
//               />
//             )
//           )
//         )}
//       </SecondaryBase>
//     </div>
//   );
// };

// export { SelfieFan };